import { isValidDate } from '../general';

export const dicomDateStringRegex = /(\d{4}).*?(\d{2}).*?(\d{2})/; // YYYYMMDD

export function getYearFromDicomDateString(dicomDateString) {
  return dicomDateString && dicomDateString.substring(0, 4); // dicomDateString is YYYYMMDD
}

export function getMonthFromDicomDateString(dicomDateString) {
  return dicomDateString && dicomDateString.substring(4, 6); // dicomDateString is YYYYMMDD
}

export function getDayFromDicomDateString(dicomDateString) {
  return dicomDateString && dicomDateString.substring(6, 8); // dicomDateString is YYYYMMDD
}

// "20181201" -> new Date(2018, 11, 01)
// NOTE: JS date object contructor month index is 0-based!!!!!
export function dicomDateStringToDate(dicomDateString) {
  if (!dicomDateString) {
    return null;
  }

  // check if already date object
  if (isValidDate(dicomDateString)) {
    return dicomDateString;
  }

  const matches = dicomDateString.match(dicomDateStringRegex);
  if (!matches) {
    return null;
  }

  const [, year, month, day] = matches;

  return new Date(Date.UTC(year, parseInt(month, 10) - 1, day));
}

// new Date(2018, 11, 01) -> "2018-12-01"
export function dateToISO8601DateString(date) {
  if (!date) {
    return '';
  }

  return date.toISOString().split('T')[0];
}

export function isISO8601ValidDate(iso8601DateString) {
  if (!iso8601DateString) {
    return false;
  }

  if (isValidDate(iso8601DateString)) {
    return true;
  }

  let isValid = false;
  const matches = iso8601DateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (matches !== null) {
    const year = +matches[1];
    const month = +matches[2] - 1; // NOTE: month index is 0-based
    const day = +matches[3];
    const date = new Date(year, month, day);

    // JS Date constructor creates valid dates if provided values outside expected range
    // e.g. 2018-14-01 -> 2019-03-01
    // so check that values are not overflowed
    if (date.getMonth() !== month || date.getDate() !== day) {
      return false;
    }

    isValid = isValidDate(date);
  }

  return isValid;
}

// This function is safe to use on dates that are already dicom date strings
export function iso8601StringToDicomDateString(isoDateString) {
  if (!isoDateString) {
    return '';
  }

  return isoDateString.slice(0, 10).replace(/-/g, '');
}

export const isScanExpired = scanDate => {
  if (!scanDate) {
    return false;
  }

  const threeMonthsAgo = new Date();
  const parsedScanDate = dicomDateStringToDate(scanDate);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  if (parsedScanDate < threeMonthsAgo) {
    return true;
  }
  return false;
};

export function spellOutPatientSex(sexAbbrev) {
  return (
    {
      M: 'Male',
      F: 'Female',
      O: 'Other',
    }[sexAbbrev] || ''
  );
}

export function formatPatientName(name) {
  if (!name) {
    return name;
  }

  let formattedName = name.replace(/[\^=]/gi, ' ');
  formattedName = formattedName.trim();
  formattedName = formattedName.replace(/^[,_]*|[,_]*$/g, '');
  formattedName = formattedName.trim();

  return formattedName;
}

export function isScanAnonymized(scanInfo = {}) {
  if (!scanInfo.patient_name || !scanInfo.patient_mrn || !scanInfo.patientDOB) {
    return true;
  }

  if (
    /patient|anonymous/i.test(scanInfo.patient_name) ||
    /patient|anonymous/i.test(scanInfo.patient_mrn)
  ) {
    return true;
  }

  return false;
}

export function isVolAnonymized(volInfo = {}) {
  if (!volInfo.patientName || !volInfo.patientMRN || !volInfo.patientDOB) {
    return true;
  }

  if (
    /patient|anonymous/i.test(volInfo.patientName) ||
    /patient|anonymous/i.test(volInfo.patientMRN)
  ) {
    return true;
  }

  return false;
}

function PatientPersonalInfo() {
  this.matchingInfo = false;
  this.matchingName = false;
  this.matchingMRN = false;
  this.matchingDOB = false;
  this.volName = '';
  this.scanName = '';
  this.volMRN = '';
  this.scanMRN = '';
  this.volDOB = '';
  this.scanDOB = '';
}

export function PatientInfoResults() {
  this.personalInfo = new PatientPersonalInfo();
  this.isVolAnonymized = false;
  this.isScanAnonymized = false;
  this.someVolHasConfirmedInfo = false;
  this.someVolHasUnconfirmedInfo = false;
  this.isValid = true;
}

const formatVolDOB = volDOB => {
  if (!volDOB?.length) {
    return '';
  }

  const dateParts = volDOB.split('-');
  if (dateParts.length !== 3) {
    return '';
  }

  const year = dateParts[0];
  const month = dateParts[1];
  const day = dateParts[2].substring(0, 2);

  return `${year}-${month}-${day}`;
};

function verifyPersonalInfoMatch(vol, scan) {
  const pInfo = new PatientPersonalInfo();

  pInfo.volName = vol.patientName;
  pInfo.scanName = scan.patient_name;
  pInfo.matchingName = pInfo.volName === pInfo.scanName;

  pInfo.volMRN = vol.patientMRN;
  pInfo.scanMRN = scan.patient_mrn;
  pInfo.matchingMRN = pInfo.volMRN === pInfo.scanMRN;

  pInfo.volDOB = formatVolDOB(vol.patientDOB);
  pInfo.scanDOB = scan.patientDOB.substring(0, scan.patientDOB.indexOf('T'));
  pInfo.matchingDOB = pInfo.volDOB === pInfo.scanDOB;

  pInfo.matchingInfo = pInfo.matchingName && pInfo.matchingMRN && pInfo.matchingDOB;

  return pInfo;
}

export function verifyPatientDBInfo(vol, scan, ...scanVols) {
  const patientInfoResults = new PatientInfoResults();

  if (!vol || !scan) {
    patientInfoResults.isValid = false;
    return patientInfoResults;
  }

  patientInfoResults.isVolAnonymized = isVolAnonymized(vol);

  // Skip if DB patient info is anonymized
  if (isScanAnonymized(scan)) {
    patientInfoResults.isScanAnonymized = true;
    return patientInfoResults;
  }

  patientInfoResults.personalInfo = verifyPersonalInfoMatch(vol, scan);

  if (scanVols) {
    patientInfoResults.someVolHasConfirmedInfo = scanVols.some(
      scanVol => scanVol.some(v => verifyPersonalInfoMatch(v, scan).matchingInfo, scan),
      scan,
    );

    patientInfoResults.someVolHasUnconfirmedInfo = scanVols.some(
      scanVol =>
        scanVol.some(
          v => !verifyPersonalInfoMatch(v, scan).matchingInfo && !isVolAnonymized(v),
          scan,
        ),
      scan,
    );
  }

  return patientInfoResults;
}

export function isValidPatientName(patientName) {
  if (!patientName) {
    return false;
  }

  return !!patientName.trim().length;
}

export function isValidPatientMRN(patientMRN) {
  if (!patientMRN) {
    return false;
  }

  return !!patientMRN.trim().length;
}

export function isValidPatientDOB(patientDOB) {
  return isISO8601ValidDate(patientDOB);
}

export function isValidPatientSex(patientSex) {
  if (!patientSex) {
    return false;
  }

  const allowedValues = ['M', 'F', 'O'];
  return allowedValues.includes(patientSex.toUpperCase());
}

export function isValidScanDate(scanDate) {
  return isISO8601ValidDate(scanDate);
}
